/**
 * Notification contracts (M5 task 7; 03-modules.md platform "notifications", 02-architecture.md
 * "Notifications", 07-connectors.md messaging, 04-data-model.md notifications tables).
 *
 * Templates per message and language, per-outlet channel settings, opt-outs (DPDP), the delivery
 * log, test sends, and the events the worker flow runs on. Messages go out through the
 * outlet's messaging provider route (connectors); `terminal` popups go over the realtime hub.
 *
 * Personal data: a recipient is held in clear text only while a message waits for delivery.
 * The log, the opt-out list, audit rows and events carry a masked form and a hash.
 */
import { z } from 'zod';
import { isoTimestampSchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import { nonNegativePaiseSchema, paiseSchema } from './money.js';

// ---------------------------------------------------------------------------
// Vocabulary (the catalogue itself, with variables and defaults, is packages/domain)

export const NOTIFICATION_CHANNELS = ['whatsapp', 'sms', 'email', 'terminal'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];
export const PROVIDER_CHANNELS = ['whatsapp', 'sms', 'email'] as const;
export type ProviderChannel = (typeof PROVIDER_CHANNELS)[number];

export const NOTIFICATION_LANGUAGES = [
  'en',
  'hi',
  'ta',
  'te',
  'kn',
  'ml',
  'mr',
  'bn',
  'gu',
] as const;
export type NotificationLanguage = (typeof NOTIFICATION_LANGUAGES)[number];
export const LANGUAGE_LABEL: Readonly<Record<NotificationLanguage, string>> = {
  en: 'English',
  hi: 'Hindi',
  ta: 'Tamil',
  te: 'Telugu',
  kn: 'Kannada',
  ml: 'Malayalam',
  mr: 'Marathi',
  bn: 'Bengali',
  gu: 'Gujarati',
};

export const NOTIFICATION_CATEGORIES = [
  'security',
  'order',
  'reminder',
  'alert',
  'popup',
  'test',
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export const OPT_OUT_CATEGORIES = ['order', 'reminder'] as const;
export type OptOutCategory = (typeof OPT_OUT_CATEGORIES)[number];

export const NOTIFICATION_EVENT_KEYS = [
  'auth.otp',
  'order.accepted',
  'order.rejected',
  'order.ready',
  'order.out_for_delivery',
  'order.cancelled',
  'order.receipt',
  'khata.reminder',
  'ops.alert',
  'popup.qr_order',
  'popup.low_stock',
  'popup.payment_received',
  'test.message',
] as const;
export type NotificationEventKey = (typeof NOTIFICATION_EVENT_KEYS)[number];

/**
 * `queued` waiting for the worker; `retrying` a provider attempt failed, another is coming;
 * `sent` the provider accepted it; `delivered` / `read` the provider reported it;
 * `failed` every attempt failed (a dead letter: retry it by hand); `suppressed` not sent
 * on purpose (opted out, switched off, no provider, no recipient); `cancelled` a person
 * erased before it went.
 */
export const MESSAGE_STATUSES = [
  'queued',
  'retrying',
  'sent',
  'delivered',
  'read',
  'failed',
  'suppressed',
  'cancelled',
] as const;
export type MessageStatus = (typeof MESSAGE_STATUSES)[number];

export const SUPPRESSION_REASONS = [
  'opted_out',
  'switched_off',
  'no_provider',
  'no_recipient',
  'no_template',
  'duplicate',
] as const;
export type SuppressionReason = (typeof SUPPRESSION_REASONS)[number];

const queryInt = (v: unknown) => (typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : v);
const key = z.enum(NOTIFICATION_EVENT_KEYS);
const channel = z.enum(NOTIFICATION_CHANNELS);
const language = z.enum(NOTIFICATION_LANGUAGES);

// ---------------------------------------------------------------------------
// Catalogue

export const templateVariableSchema = z.object({
  name: z.string(),
  label: z.string(),
  sample: z.string(),
});

export const notificationEventDefSchema = z.object({
  key,
  label: z.string(),
  description: z.string(),
  category: z.enum(NOTIFICATION_CATEGORIES),
  audience: z.enum(['customer', 'staff', 'terminal']),
  variables: z.array(templateVariableSchema),
  defaultChannels: z.array(channel),
  channels: z.array(channel),
  switchable: z.boolean(),
  defaultEnabled: z.boolean(),
  /** Max characters per channel. */
  maxLength: z.record(z.string(), z.int()),
});
export const notificationCatalogueSchema = z.object({
  events: z.array(notificationEventDefSchema),
  languages: z.array(z.object({ code: z.enum(NOTIFICATION_LANGUAGES), label: z.string() })),
});
export type NotificationCatalogue = z.output<typeof notificationCatalogueSchema>;

// ---------------------------------------------------------------------------
// Templates (company level)

export const templateTextSchema = z.object({
  subject: z.string().trim().min(1).max(300).nullable().optional(),
  body: z.string().min(1).max(4000),
});

export const saveTemplateSchema = z.object({
  key,
  channel,
  language,
  subject: z.string().trim().min(1).max(300).nullable().optional(),
  body: z.string().trim().min(1).max(4000),
  /** The provider's pre-approved template name or id (WhatsApp template name, SMS DLT template id). */
  providerTemplateId: z.string().trim().min(1).max(200).nullable().optional(),
});

export const messageTemplateSchema = z.object({
  key,
  channel,
  language,
  subject: z.string().nullable(),
  body: z.string(),
  providerTemplateId: z.string().nullable(),
  /** `custom`: this company's text; `builtin`: QRkey's. */
  source: z.enum(['custom', 'builtin']),
  version: z.int().nullable(),
  updatedAt: isoTimestampSchema.nullable(),
});
export type MessageTemplateView = z.output<typeof messageTemplateSchema>;

export const templateListQuerySchema = z.object({ key: key.optional() });
export const templateListSchema = z.object({ data: z.array(messageTemplateSchema) });
export const templateParamsSchema = z.object({ key, channel, language });

// ---------------------------------------------------------------------------
// Outlet settings

export const notificationSettingSchema = z.object({
  key,
  enabled: z.boolean(),
  /** Preference order: the first channel the outlet can send on is used. */
  channels: z.array(channel),
  /** `default`: the outlet never changed it. */
  source: z.enum(['default', 'custom']),
  switchable: z.boolean(),
  version: z.int().nullable(),
});
export type NotificationSettingView = z.output<typeof notificationSettingSchema>;

export const saveNotificationSettingSchema = z.object({
  enabled: z.boolean(),
  channels: z
    .array(channel)
    .min(1)
    .max(4)
    .refine((a) => new Set(a).size === a.length, { message: 'List each channel once' }),
});
export const notificationSettingParamsSchema = z.object({ outletId: uuidSchema, key });

export const khataReminderRulesSchema = z.object({
  enabled: z.boolean(),
  minDuePaise: nonNegativePaiseSchema,
  minDays: z.int().min(0).max(365),
  repeatDays: z.int().min(1).max(365),
});

export const opsAlertConfigSchema = z.object({
  enabled: z.boolean(),
  channel: z.enum(PROVIDER_CHANNELS),
  /** Where the alert goes: an email address or a phone number, by channel. Masked when read. */
  toMasked: z.string().nullable(),
});

export const notificationConfigSchema = z.object({
  outletId: uuidSchema,
  defaultLanguage: language,
  khataReminders: khataReminderRulesSchema,
  opsAlert: opsAlertConfigSchema,
  version: z.int().nullable(),
});
export type NotificationConfigView = z.output<typeof notificationConfigSchema>;

export const notificationSettingsSchema = z.object({
  config: notificationConfigSchema,
  settings: z.array(notificationSettingSchema),
  /** Channels the outlet can send on right now (an active provider on its route). */
  availableChannels: z.array(channel),
});

export const saveNotificationConfigSchema = z
  .object({
    defaultLanguage: language,
    khataReminders: khataReminderRulesSchema.partial(),
    opsAlert: z.object({
      enabled: z.boolean().optional(),
      channel: z.enum(PROVIDER_CHANNELS).optional(),
      /** The address or phone alerts go to; `null` clears it. */
      to: z.string().trim().min(3).max(254).nullable().optional(),
    }),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });

// ---------------------------------------------------------------------------
// Delivery log

export const messageLogSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  eventKey: key,
  category: z.enum(NOTIFICATION_CATEGORIES),
  channel,
  language,
  toMasked: z.string().nullable(),
  status: z.enum(MESSAGE_STATUSES),
  attempts: z.int(),
  /** A provider error code or the reason it was suppressed. */
  detail: z.string().nullable(),
  providerRef: z.string().nullable(),
  refType: z.string().nullable(),
  refId: uuidSchema.nullable(),
  createdAt: isoTimestampSchema,
  sentAt: isoTimestampSchema.nullable(),
  deliveredAt: isoTimestampSchema.nullable(),
  updatedAt: isoTimestampSchema,
});
export type MessageLogView = z.output<typeof messageLogSchema>;

export const messageLogQuerySchema = z.object({
  status: z.enum(MESSAGE_STATUSES).optional(),
  channel: channel.optional(),
  eventKey: key.optional(),
  limit: z.preprocess(queryInt, z.number().int().min(1).max(200).default(50)),
  offset: z.preprocess(queryInt, z.number().int().min(0).max(1_000_000).default(0)),
});
export const messageLogListSchema = z.object({
  data: z.array(messageLogSchema),
  total: z.int(),
  nextOffset: z.int().nullable(),
  /** Counts by status over the same filter (without status). */
  counts: z.record(z.string(), z.int()),
});
export const messageLogParamsSchema = z.object({ outletId: uuidSchema, messageId: uuidSchema });

export const testSendSchema = z.object({
  id: uuidSchema.optional(),
  channel,
  /** A phone number or email address (not used for the terminal channel). */
  to: z.string().trim().min(3).max(254).optional(),
  language: language.optional(),
});
export const testSendResultSchema = z.object({
  messageId: uuidSchema,
  status: z.enum(MESSAGE_STATUSES),
});

// ---------------------------------------------------------------------------
// Opt-outs (DPDP)

export const optOutSchema = z.object({
  id: uuidSchema,
  toMasked: z.string(),
  channel: z.enum([...NOTIFICATION_CHANNELS, 'all']),
  category: z.enum([...OPT_OUT_CATEGORIES, 'all']),
  source: z.enum(['customer', 'staff']),
  note: z.string().nullable(),
  createdAt: isoTimestampSchema,
  revokedAt: isoTimestampSchema.nullable(),
});
export type OptOutView = z.output<typeof optOutSchema>;

export const recordOptOutSchema = z.object({
  id: uuidSchema.optional(),
  to: z.string().trim().min(3).max(254),
  channel: z.enum([...PROVIDER_CHANNELS, 'all']).default('all'),
  category: z.enum([...OPT_OUT_CATEGORIES, 'all']).default('all'),
  source: z.enum(['customer', 'staff']).default('customer'),
  note: z.string().trim().min(1).max(300).optional(),
});
export const optOutListQuerySchema = z.object({
  includeRevoked: z.preprocess((v) => v === 'true' || v === true, z.boolean().default(false)),
  limit: z.preprocess(queryInt, z.number().int().min(1).max(200).default(50)),
  offset: z.preprocess(queryInt, z.number().int().min(0).max(1_000_000).default(0)),
});
export const optOutListSchema = z.object({ data: z.array(optOutSchema), total: z.int() });
export const optOutParamsSchema = z.object({ optOutId: uuidSchema });

// ---------------------------------------------------------------------------
// Events (no personal data)

const messageRef = {
  messageId: uuidSchema,
  eventKey: key,
  channel,
  category: z.enum(NOTIFICATION_CATEGORIES),
};

export const notificationQueuedV1 = defineEvent(
  'notification.queued',
  1,
  z.object({ ...messageRef, attempt: z.int().min(1) }),
);
export const notificationSentV1 = defineEvent(
  'notification.sent',
  1,
  z.object({ ...messageRef, providerRef: z.string().nullable(), attempts: z.int() }),
);
export const notificationAttemptFailedV1 = defineEvent(
  'notification.attempt_failed',
  1,
  z.object({ ...messageRef, attempts: z.int(), errorCode: z.string(), final: z.boolean() }),
);
export const notificationSuppressedV1 = defineEvent(
  'notification.suppressed',
  1,
  z.object({ ...messageRef, reason: z.enum(SUPPRESSION_REASONS) }),
);
export const notificationReportedV1 = defineEvent(
  'notification.reported',
  1,
  z.object({ ...messageRef, status: z.enum(['delivered', 'read', 'failed']) }),
);
export const notificationRetryRequestedV1 = defineEvent(
  'notification.retry_requested',
  1,
  z.object({ ...messageRef }),
);
export const notificationScrubbedV1 = defineEvent(
  'notification.scrubbed',
  1,
  z.object({ customerId: uuidSchema.nullable(), messages: z.int() }),
);
export const notificationTemplateSavedV1 = defineEvent(
  'notification.template_saved',
  1,
  z.object({
    key,
    channel,
    language,
    change: z.enum(['saved', 'reset']),
    version: z.int().nullable(),
  }),
);
export const notificationSettingSavedV1 = defineEvent(
  'notification.setting_saved',
  1,
  z.object({ key, enabled: z.boolean(), channels: z.array(channel), version: z.int() }),
);
export const notificationConfigSavedV1 = defineEvent(
  'notification.config_saved',
  1,
  z.object({ fields: z.array(z.string()), version: z.int() }),
);
export const optOutRecordedV1 = defineEvent(
  'notification.opt_out_recorded',
  1,
  z.object({
    optOutId: uuidSchema,
    channel: z.enum([...NOTIFICATION_CHANNELS, 'all']),
    category: z.enum([...OPT_OUT_CATEGORIES, 'all']),
    source: z.enum(['customer', 'staff']),
  }),
);
export const optOutRevokedV1 = defineEvent(
  'notification.opt_out_revoked',
  1,
  z.object({ optOutId: uuidSchema }),
);
export const opsAlertRaisedV1 = defineEvent(
  'ops_alert.raised',
  1,
  z.object({
    dedupeKey: z.string(),
    kind: z.string(),
    outcome: z.enum(['sent', 'deduped', 'rate_limited', 'not_configured']),
    count: z.int(),
    messageId: uuidSchema.nullable(),
  }),
);

/** What a till shows: a toast with a short text. Outlet-wide room, so nothing personal. */
export const POPUP_SEVERITIES = ['info', 'warning', 'success'] as const;
export const popupRaisedV1 = defineEvent(
  'popup.raised',
  1,
  z.object({
    messageId: uuidSchema,
    kind: z.enum(['popup.qr_order', 'popup.low_stock', 'popup.payment_received', 'test.message']),
    severity: z.enum(POPUP_SEVERITIES),
    text: z.string().max(200),
    orderId: uuidSchema.nullable(),
  }),
);

export const NOTIFICATION_EVENTS = [
  notificationQueuedV1,
  notificationSentV1,
  notificationAttemptFailedV1,
  notificationSuppressedV1,
  notificationReportedV1,
  notificationRetryRequestedV1,
  notificationScrubbedV1,
  notificationTemplateSavedV1,
  notificationSettingSavedV1,
  notificationConfigSavedV1,
  optOutRecordedV1,
  optOutRevokedV1,
  opsAlertRaisedV1,
  popupRaisedV1,
] as const;

// ---------------------------------------------------------------------------
// The receipt a message links to (public, signed link: GET /v1/public/receipts/{token})

export const receiptLineSchema = z.object({
  name: z.string(),
  /** Quantity in thousandths (1 item is 1000). */
  qtyMilli: z.int(),
  totalPaise: paiseSchema,
});
export const receiptViewSchema = z.object({
  outletName: z.string(),
  orderNo: z.int(),
  businessDate: z.string(),
  status: z.string(),
  invoiceNo: z.string().nullable(),
  lines: z.array(receiptLineSchema),
  totalPaise: paiseSchema,
  paidPaise: paiseSchema,
});
export type ReceiptView = z.output<typeof receiptViewSchema>;
export const receiptParamsSchema = z.object({ token: z.string().min(10).max(200) });
